/**
 * The one connections table, and the contract every provider stored in it
 * agrees to.
 *
 * WHY ONE TABLE
 *
 * Each integration used to bring its own: google_connections,
 * microsoft_connections, fortnox_connections, bokio_connections,
 * icloud_credentials, oauth_tokens, and the Vitec pair after them. Every
 * migration re-stated the same RLS policies, the same column-level GRANT
 * hiding the sealed columns, the same audit table and the same NOTIFY
 * trigger, and every registry (secret columns, admin list, tool gating,
 * refresh sweeper) grew one entry per provider. Nothing ever searched those
 * tables by anything but instance, provider and id.
 *
 * So the shape is a keyed store: instance × provider × tenant, with the
 * provider-specific parts as TYPED JSON — a `metadata` document for what the
 * web and the runtime read, and one sealed `secret` document for what only
 * the runtime opens. Both are interpreted per provider through the types
 * below; the database holds them as jsonb and bytea and knows nothing.
 *
 * WHAT STAYS A COLUMN
 *
 * Only what is filtered on: `status` (every list, every gate) and
 * `attention_at` (the refresh sweeper's and the probe runner's "look at this
 * row by" clock). `tenant_key` is a column because the uniqueness rule
 * (one row per tenant per instance) has to be a constraint, and `label`
 * because the admin list and every pane show it without knowing the
 * provider. Everything else is `metadata`.
 *
 * ADDING A PROVIDER
 *
 * Add its name to `INTEGRATION_PROVIDERS`, declare its metadata and secret
 * types here, say whose its secret is in `INTEGRATION_SECRET_OWNER` (the
 * compiler insists), and write its own module over the generic runtime store
 * (`apps/runtime/src/integrations/connections.ts`). No migration, no other
 * registry entries: the secret registry, the RLS, the audit table, the
 * NOTIFY channel and the admin source all already cover the table.
 *
 * VERSIONING THE DOCUMENTS
 *
 * Both documents carry a `schema` tag (`"vitec/1"`, `"vitec-secret/1"`).
 * The provider is already a column, so the tag carries only what the column
 * cannot: which SHAPE this document has. It lives inside the document, not
 * beside it, because the sealed one has to be self-describing on its own —
 * in a backup, in a `content-debug reveal`, in a re-seal job that only has
 * the plaintext in hand. Three rules:
 *
 *   1. Every parser switches on `schema`, treats a missing tag as version 1,
 *      and returns the CURRENT shape, upgrading older versions on read. A
 *      single old row never blocks a deploy.
 *   2. Metadata is plain jsonb and a shape change can be rewritten in a
 *      migration. The secret is ciphertext and cannot: a secret-shape change
 *      ships with a re-seal job (the `crypto/backfill.ts` pattern) that
 *      opens, upgrades and re-seals every row, after which the old branch in
 *      the parser is deleted.
 *   3. The two documents version independently. Adding a metadata field must
 *      never force a re-seal of every credential.
 *
 * Vitec is the first tenant (V361). Bokio is the second and the first to
 * MOVE here (the Bokio section below records how); Fortnox follows. Each is its own cutover: their sealed blobs are bound to their
 * old table name in the AAD and must be opened and re-sealed under this one.
 */

import type { AreaPermissions } from "./area-permissions";
import { googleAccessOrDisabled, type GoogleAccess } from "../google/scopes";
import { microsoftAccessOrDisabled, type MicrosoftAccess } from "../microsoft/scopes";
import { parseVitecReachRequest, type VitecReachRequest } from "../vitec/reach";
import type { IntegrationState } from "./state";

export const INTEGRATION_PROVIDERS = ["vitec", "bokio", "fortnox", "oqto", "spiris", "wint", "icloud", "google", "microsoft", "oauth_api"] as const;

export type IntegrationProvider = (typeof INTEGRATION_PROVIDERS)[number];

export const isIntegrationProvider = (value: string): value is IntegrationProvider =>
  (INTEGRATION_PROVIDERS as readonly string[]).includes(value);

/**
 * Whose credential a connection's sealed secret is. `customer`: something the
 * customer typed in and could use elsewhere (iCloud's app-specific password).
 * `squidler`: tokens issued to OUR client registration, or our partner
 * credential — useless outside Squidler and not the customer's to take. The
 * customer data export hands over only `customer` secrets
 * (apps/runtime/src/export/catalogue.ts).
 */
export const INTEGRATION_SECRET_OWNER: Record<IntegrationProvider, "customer" | "squidler"> = {
  vitec: "squidler",
  bokio: "squidler",
  fortnox: "squidler",
  oqto: "squidler",
  spiris: "squidler",
  // An Egen app's username and API key, created by the customer in Wint and
  // pasted in — like iCloud's password, theirs to take with them.
  wint: "customer",
  icloud: "customer",
  google: "squidler",
  microsoft: "squidler",
  // Tokens issued to whichever client signed in. With the customer's own app
  // they could re-create every piece in their provider console; the export
  // does not hand over live tokens either way.
  oauth_api: "squidler",
};

/** The columns every reader gets, provider-agnostic. */
export interface ConnectionRecord<M> {
  readonly id: string;
  readonly instanceId: string;
  readonly provider: IntegrationProvider;
  /** The provider's identity for the tenant, normalised (see each provider's type). */
  readonly tenantKey: string;
  /** Display name — office, company, mailbox. */
  readonly label: string;
  readonly status: IntegrationState;
  readonly metadata: M;
  /** Whether the row carries a sealed secret document — derived from the
   *  column, so "open the DEK only when there is something to open" needs no
   *  flag a provider could let drift. */
  readonly hasSecret: boolean;
  /**
   * What a Squidler operator approved of the connection's requested reach,
   * unparsed; null = not decided. Vitec only so far (`@shared/vitec/reach`,
   * V485). A column, not metadata, because the customer may write metadata.
   */
  readonly approvedReach: unknown;
  /** When the runtime should next look at this row (refresh, probe); null = never. */
  readonly attentionAt: Date | null;
  readonly lastUsedAt: Date | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
}

// ── Vitec ──────────────────────────────────────────────────────────────────

/** The shape this code writes. Bump only with a parser branch for the old one. */
export const VITEC_METADATA_SCHEMA = "vitec/1";
export const VITEC_SECRET_SCHEMA = "vitec-secret/1";

/** The tag on an untrusted document; null when absent (= version 1). */
export const schemaOf = (value: unknown): string | null =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    && typeof (value as { schema?: unknown }).schema === "string"
    ? (value as { schema: string }).schema
    : null;

/**
 * `tenant_key` = the Vitec customer id, upper-cased (`M30011`, `G2`).
 * `label` = the office name. Stored with `schema: VITEC_METADATA_SCHEMA`.
 */
export interface VitecConnectionMetadata {
  /** `G2` when the office belongs to a chain we have seen. */
  readonly group_id: string | null;
  /**
   * Complete area selection — what the customer WANTS; absent = not
   * connected. What the agent may actually do is this capped by the office's
   * consent on the organisation's grant (`@shared/vitec/consent`, V482).
   */
  readonly area_permissions: AreaPermissions;
  /**
   * Area → when the customer last asked for it, or for more of it (switched
   * on, raised to write, or "ask again"). A request newer than the grant's
   * decision makes a declined area pending again. Additive, no schema bump.
   * (Until V482 the row also carried `granted_scopes`, inferred by the probe;
   * old rows keep the key and nothing reads it.)
   */
  readonly area_requested_at: Readonly<Record<string, string>>;
  /**
   * Evidence that the connecting user belongs to the office: the probe found
   * their login e-mail among its Express users. Since V459 this activates
   * nothing — an operator's organisation grant does — and is shown to that
   * operator.
   */
  readonly verified_by: "user_email_match" | null;
  /** When the probe last saw Vitec answer for the office; null while Vitec
   *  has not activated it (or has withdrawn it). Additive, no schema bump. */
  readonly activated_at: string | null;
  /**
   * Why a `pending` row Vitec may already answer for is not active: Squidler
   * has not granted the organisation the office yet, has withdrawn that
   * grant, or the agent moved to an organisation without one (V459). null =
   * not one of those — i.e. still waiting for Vitec. Structured so each
   * reader words it (the pane translates); never prose.
   */
  readonly pending_reason: VitecPendingReason | null;
  /** Whose login e-mail the probe matches against the office's Express users. */
  readonly verified_user_id: string | null;
  readonly verified_at: string | null;
  readonly last_probe_at: string | null;
  readonly last_probe_error: string | null;
  /** Consecutive 403s on an active row; two flip it to revoked. */
  readonly probe_failures: number;
  /** Squidler user id → Express user id, per office. */
  readonly user_links: Readonly<Record<string, { readonly vitec_user_id: string; readonly source: "email_match" | "manual" }>>;
  /**
   * Which Vitec notifications wake the agent for this office, as `Type` or
   * `Type/Subtype` (`Meeting`, `EstateContact/ViewingParticipant`). null =
   * never chosen, so the runtime's default applies (`tools/vitec/webhook.ts`);
   * an empty list is a choice and means none. Additive (no schema bump): an
   * older reader simply does not see it.
   */
  readonly notify_types: readonly string[] | null;
  /**
   * Which part of the office the customer asks this agent to see: the whole
   * office, named realtors, or the realtors its members are. null = never
   * chosen (a row from before V485 is backfilled as the whole office). What
   * the agent gets is this capped by the operator's approval
   * (`approved_reach`, a column) — `@shared/vitec/reach`. Additive.
   */
  readonly requested_reach: VitecReachRequest | null;
  /**
   * The office's Express users as the probe last listed them (archived ones
   * left out), so the settings and the operator can name realtors without
   * calling Vitec. Ids and names only. Additive.
   */
  readonly office_users: readonly VitecOfficeUser[];
}

export interface VitecOfficeUser {
  readonly vitec_user_id: string;
  readonly name: string;
}

/**
 * What goes in `integration_events.event_type` for Vitec. The column has no
 * CHECK precisely so this vocabulary can live in TypeScript; both apps write
 * it, so it lives here.
 */
/** See `VitecConnectionMetadata.pending_reason`. Written by the probe and by
 *  V459's `integration_connections_demote_ungranted`. */
export const VITEC_PENDING_REASONS = ["awaiting_grant", "grant_revoked", "org_moved"] as const;
export type VitecPendingReason = (typeof VITEC_PENDING_REASONS)[number];

/** The English wording, for the agent and the (English-only) admin. The
 *  customer's pane has its own translated copy keyed the same way. */
export const VITEC_PENDING_REASON_TEXT: Readonly<Record<VitecPendingReason, string>> = {
  awaiting_grant:
    "Vitec has activated the office. Squidler now confirms by hand that this organisation may use it; nothing more is needed from the office.",
  grant_revoked: "Squidler has withdrawn this organisation's access to the office.",
  org_moved: "The agent moved to another organisation, which Squidler has not confirmed may use this office.",
};

export const VITEC_EVENT_TYPES = [
  "requested",
  "activated",
  "settings_changed",
  "revoked",
  "probe_failed",
  "verified",
  /** An operator allowed the connection's organisation to use the office (V459). */
  "granted",
  /** ...and took that back; the connection drops to pending. */
  "grant_revoked",
  /** An operator recorded what the office's consent covers — areas activated
   *  or declined (V482); a new grant row supersedes the previous one. */
  "consent_recorded",
  "policy_violation",
  /** An operator approved what part of the office the agent may see (V485). */
  "reach_approved",
  "credential_set",
  /** A request sent although it did not match Vitec's schema, with the agent's
   *  reason. Since V479 only for refusals the schema itself can have wrong
   *  (tools/vitec/client.ts `OVERRIDABLE`), and followed by a `patch_spec`. */
  "schema_override",
] as const;

export type VitecEventType = (typeof VITEC_EVENT_TYPES)[number];

/**
 * The sealed document for a Vitec connection: per-office passwords keyed by
 * consent category or `*`. Present only for an office Vitec keys separately;
 * the shared partner credential lives in the env. On the wire it is the
 * envelope `{ schema: "vitec-secret/1", passwords: {...} }`; this is the
 * parsed `passwords` map.
 */
export type VitecConnectionSecret = Readonly<Partial<Record<string, {
  readonly username: string;
  readonly password: string;
}>>>;

/** The wire form `storeVitecCredential` writes. */
export const sealedVitecSecret = (passwords: VitecConnectionSecret): unknown =>
  ({ schema: VITEC_SECRET_SCHEMA, passwords });

// ── Parsing ────────────────────────────────────────────────────────────────

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const str = (v: unknown): string | null => (typeof v === "string" ? v : null);
const num = (v: unknown, fallback: number): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback);
const strings = (v: unknown): readonly string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

/**
 * Read an untrusted jsonb document as Vitec metadata. Total: a missing or
 * malformed field takes its empty value, so a row written by an older
 * version reads rather than throws — the same reasoning as
 * `parseAreaPermissions`.
 *
 * Only one shape exists so far (a missing tag IS version 1). When a second
 * one does, this switches on `schemaOf(value)` and upgrades the old shape
 * here, once, rather than in every reader. A tag this code has never seen
 * — a row written by a newer deploy during a blue-green overlap — is read
 * as the latest known shape: metadata changes are additive by rule 3 above,
 * so the fields this version knows are still where it expects them.
 */
export const parseVitecMetadata = (
  value: unknown,
  parseAreas: (v: unknown) => AreaPermissions,
): VitecConnectionMetadata => {
  const m = isRecord(value) ? value : {};
  const verifiedBy = str(m.verified_by);
  const links = isRecord(m.user_links) ? m.user_links : {};
  return {
    group_id: str(m.group_id),
    area_permissions: parseAreas(m.area_permissions),
    area_requested_at: isRecord(m.area_requested_at)
      ? Object.fromEntries(Object.entries(m.area_requested_at).flatMap(([k, v]) => (typeof v === "string" ? [[k, v]] : [])))
      : {},
    // A legacy `ops` (the per-connection override V459 replaced) reads as
    // null: the match is evidence only now, and re-running it is harmless.
    verified_by: verifiedBy === "user_email_match" ? verifiedBy : null,
    verified_user_id: str(m.verified_user_id),
    verified_at: str(m.verified_at),
    activated_at: str(m.activated_at),
    pending_reason: VITEC_PENDING_REASONS.find((r) => r === m.pending_reason) ?? null,
    last_probe_at: str(m.last_probe_at),
    last_probe_error: str(m.last_probe_error),
    probe_failures: num(m.probe_failures, 0),
    notify_types: Array.isArray(m.notify_types) ? strings(m.notify_types) : null,
    requested_reach: parseVitecReachRequest(m.requested_reach),
    office_users: Array.isArray(m.office_users)
      ? m.office_users.flatMap((u) => {
        if (!isRecord(u)) return [];
        const id = str(u.vitec_user_id);
        return id === null ? [] : [{ vitec_user_id: id, name: str(u.name) ?? id }];
      })
      : [],
    user_links: Object.fromEntries(
      Object.entries(links).flatMap(([userId, link]) => {
        if (!isRecord(link)) return [];
        const vitecUserId = str(link.vitec_user_id);
        if (vitecUserId === null) return [];
        return [[userId, { vitec_user_id: vitecUserId, source: link.source === "manual" ? "manual" as const : "email_match" as const }]];
      }),
    ),
  };
};

/**
 * Read an opened secret document. Unlike metadata, an UNKNOWN schema here
 * yields no passwords at all: the document is credentials, and guessing at
 * the shape of a credential is worse than falling back to the shared env
 * password (or to "waiting on Squidler"). The runtime logs the tag it did
 * not recognise.
 */
export const parseVitecSecret = (value: unknown): VitecConnectionSecret => {
  const schema = schemaOf(value);
  if (schema !== null && schema !== VITEC_SECRET_SCHEMA) return {};
  const envelope = isRecord(value) ? value : {};
  const m = isRecord(envelope.passwords) ? envelope.passwords : {};
  return Object.fromEntries(
    Object.entries(m).flatMap(([scope, cred]) => {
      if (!isRecord(cred)) return [];
      const username = str(cred.username);
      const password = str(cred.password);
      if (username === null || password === null) return [];
      return [[scope, { username, password }]];
    }),
  );
};

// ── Bokio ──────────────────────────────────────────────────────────────────
//
// Bokio had its own table (`bokio_connections`, V318) and was the first
// provider to MOVE here, in three steps that are the template for the next
// one (Fortnox): (A) every reader and writer learned both homes, the shared
// row authoritative if it existed; (B) a runtime job re-sealed each legacy
// row into this table UNDER THE REFRESH LOCK and UNDER THE SAME ROW ID — the
// lock is keyed on the id, so the live refresh path and the job serialised on
// one key, which is what made it safe against a single-use refresh token;
// (C) the legacy branches were removed, and one release later the tables
// dropped (a drop cannot ship with the code that stops reading, because
// migrations run minutes before the app swaps). Steps A and B are in git
// history: PR #1099 and `scripts/migrate-bokio-connections.ts`.

export const BOKIO_METADATA_SCHEMA = "bokio/1";
export const BOKIO_SECRET_SCHEMA = "bokio-secret/1";

/**
 * `tenant_key` = Bokio's company id, a uuid in Postgres' lower-case text
 * form (`normalizeBokioCompanyId`). `label` = the company name.
 * `attention_at` = when the refresh token dies if unused (30 days after the
 * last refresh) — the sweeper's clock. NULL there means "lifetime unknown,
 * refresh on use, do not sweep", NOT "look now".
 */
export interface BokioConnectionMetadata {
  readonly organization_number: string | null;
  readonly granted_scopes: readonly string[];
  /** Complete area selection; absent = not connected. */
  readonly area_permissions: AreaPermissions;
  /** ISO 8601. Never filtered on, so it is not a column. */
  readonly access_token_expires_at: string | null;
  readonly last_refreshed_at: string | null;
}

/**
 * Documented in Bokio's prose docs, not its OpenAPI spec: a refresh token
 * "expires when used or if unused for more than 30 days". Both apps stamp
 * `attention_at` from it — the web at authorization, the runtime at every
 * refresh — so it is defined once.
 */
export const BOKIO_REFRESH_TOKEN_IDLE_TTL_DAYS = 30;

/** When a refresh token issued at `issuedAtMs` dies if nobody uses it. */
export const bokioRefreshDeadline = (issuedAtMs: number): Date =>
  new Date(issuedAtMs + BOKIO_REFRESH_TOKEN_IDLE_TTL_DAYS * 24 * 60 * 60 * 1000);

/** uuid has a canonical form; text does not. Every write goes through this. */
export const normalizeBokioCompanyId = (companyId: string): string => companyId.trim().toLowerCase();

/** The sealed document: the token PAIR, so the two can never be torn apart. */
export interface BokioConnectionSecret {
  readonly access_token: string | null;
  readonly refresh_token: string;
}

/** The wire form every writer seals. */
export const sealedBokioSecret = (tokens: BokioConnectionSecret): unknown =>
  ({ schema: BOKIO_SECRET_SCHEMA, ...tokens });

/** Total, like `parseVitecMetadata`: an unknown tag is read for the fields
 *  this version knows, because metadata changes are additive. */
export const parseBokioMetadata = (
  value: unknown,
  parseAreas: (v: unknown) => AreaPermissions,
): BokioConnectionMetadata => {
  const m = isRecord(value) ? value : {};
  return {
    organization_number: str(m.organization_number),
    granted_scopes: strings(m.granted_scopes),
    area_permissions: parseAreas(m.area_permissions),
    access_token_expires_at: str(m.access_token_expires_at),
    last_refreshed_at: str(m.last_refreshed_at),
  };
};

/**
 * Read an opened secret document, or null when it cannot be trusted: an
 * unknown schema, or no refresh token. Never a guess at a credential — the
 * caller treats null as "this connection has no usable secret" and says so.
 */
export const parseBokioSecret = (value: unknown): BokioConnectionSecret | null => {
  const schema = schemaOf(value);
  if (schema !== null && schema !== BOKIO_SECRET_SCHEMA) return null;
  const m = isRecord(value) ? value : {};
  const refreshToken = str(m.refresh_token);
  if (refreshToken === null || refreshToken === "") return null;
  return { access_token: str(m.access_token), refresh_token: refreshToken };
};

// ── Fortnox ────────────────────────────────────────────────────────────────
//
// Moved here on 2026-09-23 by the three steps Bokio established (above), each
// row keeping its id so the refresh lock and the move serialised on one key.
//
// The secret document below is now the FALLBACK rather than the credential.
// Fortnox mints an access token from our own client id and secret plus the
// company number (`tools/fortnox/service-token.ts`), so the ordinary path
// holds nothing per connection that can be spent or lost.

export const FORTNOX_METADATA_SCHEMA = "fortnox/1";
export const FORTNOX_SECRET_SCHEMA = "fortnox-secret/1";

/**
 * `tenant_key` = Fortnox's database number, as decimal digits. `label` = the
 * company name. `attention_at` = when the refresh token dies (45 days after
 * it was issued, whether used or not) — the sweeper's clock; never NULL for
 * Fortnox, since every token has had a known lifetime from the start.
 */
export interface FortnoxConnectionMetadata {
  readonly organization_number: string | null;
  readonly granted_scopes: readonly string[];
  /** Per-area write permission; an area absent from the map is read. */
  readonly area_permissions: AreaPermissions;
  /** ISO 8601. Never filtered on, so it is not a column. */
  readonly access_token_expires_at: string | null;
  readonly last_refreshed_at: string | null;
}

/** Documented by Fortnox: a refresh token is valid 45 days from issue. */
export const FORTNOX_REFRESH_TOKEN_TTL_DAYS = 45;

/** When a refresh token issued at `issuedAtMs` dies. */
export const fortnoxRefreshDeadline = (issuedAtMs: number): Date =>
  new Date(issuedAtMs + FORTNOX_REFRESH_TOKEN_TTL_DAYS * 24 * 60 * 60 * 1000);

/** Fortnox's database number has one text form: the digits. */
export const normalizeFortnoxDatabaseNumber = (databaseNumber: string | number): string =>
  String(databaseNumber).trim();

/** The sealed document: the token PAIR, so the two can never be torn apart. */
export interface FortnoxConnectionSecret {
  readonly access_token: string | null;
  readonly refresh_token: string;
}

export const sealedFortnoxSecret = (tokens: FortnoxConnectionSecret): unknown =>
  ({ schema: FORTNOX_SECRET_SCHEMA, ...tokens });

export const parseFortnoxMetadata = (
  value: unknown,
  parseAreas: (v: unknown) => AreaPermissions,
): FortnoxConnectionMetadata => {
  const m = isRecord(value) ? value : {};
  return {
    organization_number: str(m.organization_number),
    granted_scopes: strings(m.granted_scopes),
    area_permissions: parseAreas(m.area_permissions),
    access_token_expires_at: str(m.access_token_expires_at),
    last_refreshed_at: str(m.last_refreshed_at),
  };
};

/** As `parseBokioSecret`: an unknown schema or a missing refresh token is null, never a guess. */
export const parseFortnoxSecret = (value: unknown): FortnoxConnectionSecret | null => {
  const schema = schemaOf(value);
  if (schema !== null && schema !== FORTNOX_SECRET_SCHEMA) return null;
  const m = isRecord(value) ? value : {};
  const refreshToken = str(m.refresh_token);
  if (refreshToken === null || refreshToken === "") return null;
  return { access_token: str(m.access_token), refresh_token: refreshToken };
};

// ── Oqto ───────────────────────────────────────────────────────────────────
//
// The first provider to start here with NO SECRET DOCUMENT AT ALL. Oqto's
// authorization ends in a `company_id`, not a token: an access token is
// minted on demand from our own client id and secret plus that company id
// (client credentials, ten minutes, `@shared/oqto/oauth.ts`), which is the
// path Fortnox arrived at after a long detour through refresh tokens. So a
// row carries nothing that can be spent, rotated or lost — `secret_sealed`
// stays NULL, `attention_at` stays NULL (nothing to sweep), and re-activating
// the same company on a second agent disturbs nothing on the first.

export const OQTO_METADATA_SCHEMA = "oqto/1";

/**
 * `tenant_key` = Oqto's company id, a uuid in Postgres' lower-case text form
 * (`normalizeOqtoCompanyId`). `label` = the company name from
 * `/company/v1/organization`.
 *
 * `granted_scopes` holds the scopes of the areas the user included
 * (`resolveOqtoScopes`), which is what the runtime mints the company's
 * token with. Consent asked for every scope, so this is a narrowing the
 * user can widen again without a trip to Oqto — see `@shared/oqto/scopes.ts`.
 */
export interface OqtoConnectionMetadata {
  readonly organization_number: string | null;
  /** The scopes this connection's tokens are minted with. */
  readonly granted_scopes: readonly string[];
  /** Per-area write permission; an area absent from the map is read. */
  readonly area_permissions: AreaPermissions;
  /** How the company id reached us: the OAuth code flow, or a user pasting
   *  the id Oqto shows after a manual activation. Informational. */
  readonly activation: "oauth" | "manual" | null;
}

const UUID_TEXT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** uuid has a canonical form; text does not. Every write goes through this. */
export const normalizeOqtoCompanyId = (companyId: string): string => companyId.trim().toLowerCase();

/** Whether a user-supplied string is a company id at all. The manual
 *  activation path takes one pasted from Oqto's UI, so this is checked before
 *  anything is minted against it. */
export const isOqtoCompanyId = (value: string): boolean => UUID_TEXT.test(normalizeOqtoCompanyId(value));

export const parseOqtoMetadata = (
  value: unknown,
  parseAreas: (v: unknown) => AreaPermissions,
): OqtoConnectionMetadata => {
  const m = isRecord(value) ? value : {};
  const activation = str(m.activation);
  return {
    organization_number: str(m.organization_number),
    granted_scopes: strings(m.granted_scopes),
    area_permissions: parseAreas(m.area_permissions),
    activation: activation === "oauth" || activation === "manual" ? activation : null,
  };
};

// ── Spiris ─────────────────────────────────────────────────────────────────
//
// Bokio's shape — a refresh/access token pair as the one sealed document,
// read and write enforced by Spiris's own scopes — with two differences that
// remove machinery rather than add it (`@shared/spiris/oauth.ts` has the
// measurements): the refresh token does not rotate, so there is no refresh
// lock, and its two-year life is fixed from issue, so there is nothing for a
// sweeper to renew and `attention_at` stays NULL.

export const SPIRIS_METADATA_SCHEMA = "spiris/1";
export const SPIRIS_SECRET_SCHEMA = "spiris-secret/1";

/**
 * `tenant_key` = the JWT `customer_id` of the company, a uuid in Postgres'
 * lower-case text form. `label` = the company name from `/companysettings`.
 */
export interface SpirisConnectionMetadata {
  readonly organization_number: string | null;
  readonly granted_scopes: readonly string[];
  /** Complete area selection; absent = not connected (the Bokio convention). */
  readonly area_permissions: AreaPermissions;
  /** Who authorized: the grant is theirs and dies with their password. */
  readonly authorized_by_email: string | null;
  /** ISO 8601. Never filtered on, so it is not a column. */
  readonly access_token_expires_at: string | null;
  readonly last_refreshed_at: string | null;
}

/** uuid has a canonical form; text does not. Every write goes through this. */
export const normalizeSpirisCompanyId = (companyId: string): string => companyId.trim().toLowerCase();

/** The sealed document: the token PAIR, so the two can never be torn apart. */
export interface SpirisConnectionSecret {
  readonly access_token: string | null;
  readonly refresh_token: string;
}

export const sealedSpirisSecret = (tokens: SpirisConnectionSecret): unknown =>
  ({ schema: SPIRIS_SECRET_SCHEMA, ...tokens });

export const parseSpirisMetadata = (
  value: unknown,
  parseAreas: (v: unknown) => AreaPermissions,
): SpirisConnectionMetadata => {
  const m = isRecord(value) ? value : {};
  return {
    organization_number: str(m.organization_number),
    granted_scopes: strings(m.granted_scopes),
    area_permissions: parseAreas(m.area_permissions),
    authorized_by_email: str(m.authorized_by_email),
    access_token_expires_at: str(m.access_token_expires_at),
    last_refreshed_at: str(m.last_refreshed_at),
  };
};

/** As `parseBokioSecret`: an unknown schema or a missing refresh token is null, never a guess. */
export const parseSpirisSecret = (value: unknown): SpirisConnectionSecret | null => {
  const schema = schemaOf(value);
  if (schema !== null && schema !== SPIRIS_SECRET_SCHEMA) return null;
  const m = isRecord(value) ? value : {};
  const refreshToken = str(m.refresh_token);
  if (refreshToken === null || refreshToken === "") return null;
  return { access_token: str(m.access_token), refresh_token: refreshToken };
};

// ── Wint ───────────────────────────────────────────────────────────────────
//
// iCloud's shape, not Spiris's: no OAuth, no token, no scopes. The customer
// creates an "Egen app" inside Wint and pastes its username and API key; every
// request carries the pair as HTTP Basic (`@shared/wint/areas.ts`). Nothing
// rotates and nothing expires on a clock, so `attention_at` stays NULL and
// there is no sweeper entry — a key that stops working is learned from a 401.

export const WINT_METADATA_SCHEMA = "wint/1";
export const WINT_SECRET_SCHEMA = "wint-secret/1";

/**
 * `tenant_key` = Wint's company id (`Company.Id` from `GET /api/Auth`, an
 * int32) as decimal text. `label` = the company name.
 */
export interface WintConnectionMetadata {
  readonly organization_number: string | null;
  /** Complete area selection; absent = not connected (the Bokio convention). */
  readonly area_permissions: AreaPermissions;
  /**
   * The Egen app's username. Not secret — it names the app, the key is what
   * authenticates. Kept here so "replace key" can start from it.
   */
  readonly username: string | null;
  /** When the key last answered `GET /api/Auth` with this company (connect or key replacement). */
  readonly verified_at: string | null;
}

/** Wint's company ids are int32s; text keys them, so every write goes through this. */
export const normalizeWintCompanyId = (companyId: number | string): string => String(companyId).trim();

/** The sealed document: the pair, so a key can never be paired with another app's username. */
export interface WintConnectionSecret {
  readonly username: string;
  readonly api_key: string;
}

export const sealedWintSecret = (secret: WintConnectionSecret): unknown =>
  ({ schema: WINT_SECRET_SCHEMA, ...secret });

export const parseWintMetadata = (
  value: unknown,
  parseAreas: (v: unknown) => AreaPermissions,
): WintConnectionMetadata => {
  const m = isRecord(value) ? value : {};
  return {
    organization_number: str(m.organization_number),
    area_permissions: parseAreas(m.area_permissions),
    username: str(m.username),
    verified_at: str(m.verified_at),
  };
};

/** As the others: an unknown schema or a missing half is null, never a guess. */
export const parseWintSecret = (value: unknown): WintConnectionSecret | null => {
  const schema = schemaOf(value);
  if (schema !== null && schema !== WINT_SECRET_SCHEMA) return null;
  const m = isRecord(value) ? value : {};
  const username = str(m.username);
  const apiKey = str(m.api_key);
  if (username === null || username === "" || apiKey === null || apiKey === "") return null;
  return { username, api_key: apiKey };
};

// ── iCloud ─────────────────────────────────────────────────────────────────
//
// The odd one, and the reason it moved here first: iCloud has no OAuth and no
// token. The credential is a 16-character app-specific password that never
// rotates, is not single-use, and expires only when a human revokes it at
// account.apple.com — which we learn about from a 401, never from Apple.
//
// So this is the only provider whose secret document was ALREADY the shape
// `integration_connections` wants: one value, not a pair. What the move
// actually buys is on the other side. iCloud used to be TWO tables —
// `icloud_access` holding a mode and an `auth_state`, `icloud_credentials`
// holding the password — with an application-enforced invariant between them
// ("auth_state = 'authorized' if and only if a credentials row exists") that
// nothing ever checked. One row cannot violate that invariant, because the
// invariant no longer exists.
//
// It also retires `auth_state = 'none'`. A trigger minted an `icloud_access`
// row for every instance ever created, so 487 of 491 rows on prod meant
// "nobody connected anything". Here, absence of a row IS not-connected, which
// is the rule every other provider already follows.

export const ICLOUD_METADATA_SCHEMA = "icloud/1";
export const ICLOUD_SECRET_SCHEMA = "icloud-secret/1";

/**
 * `tenant_key` = the Apple ID, lowercased. `label` = the Apple ID as the user
 * typed it. `attention_at` is NULL and stays NULL: nothing expires on a clock,
 * so there is no deadline for a sweeper to read. `last_used_at` carries what
 * `last_verified_at` used to.
 */
export interface ICloudConnectionMetadata {
  /** As the user typed it; `tenant_key` holds the folded form. */
  readonly apple_id: string | null;
  /** The mailbox when it differs from the Apple ID. Null = they are the same. */
  readonly mail_address: string | null;
  /** `disabled` | `read` | `write`. */
  readonly calendar: string;
  /** `disabled` | `read` | `manage` | `send`. */
  readonly mail: string;
  /**
   * Cached CalDAV calendar-home URL on the account's shard. A CACHE, not
   * state: losing it costs two extra round trips on the next call, which is
   * why it is metadata rather than anything load-bearing.
   */
  readonly calendar_home_url: string | null;
}

/**
 * One Apple ID per agent, as the old schema enforced with a primary key.
 *
 * `integration_connections` is keyed per tenant, so the schema no longer
 * carries this — but four call sites still ask for "the instance's iCloud
 * credential" with no connection id, and an unordered `rows[0]` over two rows
 * is the kind of bug that surfaces for one customer months later. The cap
 * moves into the provider module; this constant is where it is stated.
 */
export const MAX_ICLOUD_CONNECTIONS = 1;

/** Apple IDs are case-insensitive, and the old unique key folded case. */
export const normalizeAppleId = (appleId: string): string => appleId.trim().toLowerCase();

/** The sealed document: one password, and nothing else to tear apart. */
export interface ICloudConnectionSecret {
  readonly app_password: string;
}

export const sealedICloudSecret = (secret: ICloudConnectionSecret): unknown =>
  ({ schema: ICLOUD_SECRET_SCHEMA, ...secret });

export const parseICloudMetadata = (value: unknown): ICloudConnectionMetadata => {
  const m = isRecord(value) ? value : {};
  return {
    apple_id: str(m.apple_id),
    mail_address: str(m.mail_address),
    // `disabled` is the safe default for a mode we cannot read: it denies.
    calendar: str(m.calendar) ?? "disabled",
    mail: str(m.mail) ?? "disabled",
    calendar_home_url: str(m.calendar_home_url),
  };
};

/** As the others: an unknown schema or a missing password is null, never a guess. */
export const parseICloudSecret = (value: unknown): ICloudConnectionSecret | null => {
  const schema = schemaOf(value);
  if (schema !== null && schema !== ICLOUD_SECRET_SCHEMA) return null;
  const m = isRecord(value) ? value : {};
  const appPassword = str(m.app_password);
  if (appPassword === null || appPassword === "") return null;
  return { app_password: appPassword };
};

// ── Google ─────────────────────────────────────────────────────────────────
//
// Moving here from `google_connections` (V308), which was ALREADY one row per
// authorized account with an id of its own — so, like Bokio and unlike iCloud,
// a row keeps its id when it moves, and the refresh lock keyed on that id
// serialises the move job with a live refresh.
//
// Nothing about the credential changes shape: a refresh token that never
// expires and an access token cached for an hour. What changes is that the
// two stop being two sealed columns and become ONE sealed document, so a
// refresh writes pair and expiry in one statement, and that the seven modes
// stop being CHECK-constrained columns and become metadata — which is why the
// reader below coerces every one it cannot recognise to `disabled`.

export const GOOGLE_METADATA_SCHEMA = "google/1";
export const GOOGLE_SECRET_SCHEMA = "google-secret/1";

/**
 * `tenant_key` = the Google address, lowercased — the same fold the old
 * unique index `(instance_id, lower(authorized_email))` applied. `label` = the
 * address as Google most recently spelled it.
 *
 * `attention_at` stays NULL: Google refresh tokens do not expire on a clock,
 * so there is nothing for a sweeper to watch.
 */
export interface GoogleConnectionMetadata extends GoogleAccess {
  /** As Google's userinfo returned it; `tenant_key` holds the folded form. */
  readonly authorized_email: string | null;
  /** Exactly what Google granted, sorted — the truth the modes are reconciled
   *  against, since the consent screen lets a user untick scopes. */
  readonly granted_scopes: readonly string[];
  /** ISO. When the cached access token in the secret document stops working. */
  readonly access_token_expires_at: string | null;
}

/** Google addresses are case-insensitive in practice; the old index folded case. */
export const normalizeGoogleEmail = (email: string): string => email.trim().toLowerCase();

/** The sealed document. The access token is a cache and may be absent. */
export interface GoogleConnectionSecret {
  readonly refresh_token: string;
  readonly access_token: string | null;
}

export const sealedGoogleSecret = (secret: GoogleConnectionSecret): unknown =>
  ({ schema: GOOGLE_SECRET_SCHEMA, ...secret });

export const parseGoogleMetadata = (value: unknown): GoogleConnectionMetadata => {
  const m = isRecord(value) ? value : {};
  return {
    // Unknown modes read as `disabled`: in a jsonb document nothing at the
    // schema level stops a value the code does not know, and the only safe
    // reading of one is "grants nothing".
    ...googleAccessOrDisabled(m),
    authorized_email: str(m.authorized_email),
    granted_scopes: strings(m.granted_scopes),
    access_token_expires_at: str(m.access_token_expires_at),
  };
};

/** As the others: an unknown schema or a missing refresh token is null, never a guess. */
export const parseGoogleSecret = (value: unknown): GoogleConnectionSecret | null => {
  const schema = schemaOf(value);
  if (schema !== null && schema !== GOOGLE_SECRET_SCHEMA) return null;
  const m = isRecord(value) ? value : {};
  const refreshToken = str(m.refresh_token);
  if (refreshToken === null || refreshToken === "") return null;
  const accessToken = str(m.access_token);
  return { refresh_token: refreshToken, access_token: accessToken === "" ? null : accessToken };
};

// ── Microsoft 365 ──────────────────────────────────────────────────────────
//
// Moving here from `microsoft_connections` (V312), which, like Google's, was
// already one row per authorized account with an id of its own — so a row
// keeps its id when it moves, and the refresh lock keyed on that id
// (`microsoft-refresh:<id>`) serialises the move job with a live refresh.
//
// The credential is the delicate part. Microsoft ROTATES the refresh token on
// every refresh and retires the old one, so access and refresh token are one
// indivisible fact; as one sealed document they cannot be torn apart, which
// two sealed columns only avoided by always being written together.

export const MICROSOFT_METADATA_SCHEMA = "microsoft/1";
export const MICROSOFT_SECRET_SCHEMA = "microsoft-secret/1";

/**
 * `tenant_key` = the address, lowercased — the fold the old unique index
 * `(instance_id, lower(authorized_email))` applied. `label` = the address as
 * Microsoft most recently spelled it. `attention_at` stays NULL: nothing here
 * expires on a clock a sweeper could watch.
 */
export interface MicrosoftConnectionMetadata extends MicrosoftAccess {
  readonly authorized_email: string | null;
  /** Entra tenant, from the id_token. Two accounts can look alike by address
   *  alone; this is what tells the panel which organisation each belongs to. */
  readonly tid: string | null;
  /** What Microsoft says the connection holds — from the code exchange, then
   *  from every refresh response, since a tenant admin can narrow it. */
  readonly granted_scopes: readonly string[];
  /** ISO. When the cached access token in the secret document stops working. */
  readonly access_token_expires_at: string | null;
}

export const normalizeMicrosoftEmail = (email: string): string => email.trim().toLowerCase();

/** The sealed document: the rotating refresh token and the access token it
 *  last produced, always written together. */
export interface MicrosoftConnectionSecret {
  readonly refresh_token: string;
  readonly access_token: string | null;
}

export const sealedMicrosoftSecret = (secret: MicrosoftConnectionSecret): unknown =>
  ({ schema: MICROSOFT_SECRET_SCHEMA, ...secret });

export const parseMicrosoftMetadata = (value: unknown): MicrosoftConnectionMetadata => {
  const m = isRecord(value) ? value : {};
  return {
    // Unknown modes read as `disabled`, as for Google: the only safe reading of
    // a value the code does not know is "grants nothing".
    ...microsoftAccessOrDisabled(m),
    authorized_email: str(m.authorized_email),
    tid: str(m.tid),
    granted_scopes: strings(m.granted_scopes),
    access_token_expires_at: str(m.access_token_expires_at),
  };
};

/** As the others: an unknown schema or a missing refresh token is null, never a guess. */
export const parseMicrosoftSecret = (value: unknown): MicrosoftConnectionSecret | null => {
  const schema = schemaOf(value);
  if (schema !== null && schema !== MICROSOFT_SECRET_SCHEMA) return null;
  const m = isRecord(value) ? value : {};
  const refreshToken = str(m.refresh_token);
  if (refreshToken === null || refreshToken === "") return null;
  const accessToken = str(m.access_token);
  return { refresh_token: refreshToken, access_token: accessToken === "" ? null : accessToken };
};

// ── OAuth APIs (`api connect`) ─────────────────────────────────────────────
//
// Any OAuth2 (authorization code) API an agent was asked to use, signed in
// to through the same machinery as MCP and used as `{{oauth:NAME}}` in fetch
// (apps/runtime/src/oauth/api-connections.ts, V477). One row per connection
// NAME the agent chose: `tenant_key` = `label` = that name.

export const OAUTH_API_METADATA_SCHEMA = "oauth-api/1";
export const OAUTH_API_SECRET_SCHEMA = "oauth-api-secret/1";

/**
 * A connection's name: what the agent writes in `{{oauth:NAME}}`, and the
 * row's tenant_key. One source for the tool, the placeholder pattern and the
 * settings routes, so a name one of them accepts always resolves in the others.
 */
export const OAUTH_API_NAME_PATTERN = "[a-z0-9][a-z0-9_-]{0,39}";
export const OAUTH_API_NAME_RE = new RegExp(`^${OAUTH_API_NAME_PATTERN}$`);

/** Who issued the client the sign-in used. */
export type OAuthApiClientSource = "squidler" | "dcr" | "customer";

export interface OAuthApiConnectionMetadata {
  /** Registrable domains a token may be sent to (`api.github.com` → `github.com`). */
  readonly hosts: readonly string[];
  /** Domains the agent asked for that do not match the sign-in server, awaiting the owner's approval. */
  readonly pending_hosts: readonly string[];
  /** The issuer, when known: the key a Squidler-registered client is looked up by. */
  readonly authorization_server: string | null;
  readonly authorization_endpoint: string;
  readonly token_endpoint: string;
  readonly scopes: readonly string[];
  /** Null until a client has been chosen. */
  readonly client_source: OAuthApiClientSource | null;
  /** The `mcp_oauth_clients` row; set exactly when `client_source` is "squidler" (the parser enforces it). */
  readonly oauth_client_id: string | null;
  /** The provider's developer docs, when the agent knew them — shown in the "use your own app" help. */
  readonly docs_url: string | null;
  /** True while no client could be had: the owner can add their own app, or wait for Squidler's. */
  readonly client_needed: boolean;
  /**
   * Scopes the last sign-in asked for that the provider did not grant. Empty
   * when it granted them all, or did not say. A provider that lets software
   * register itself often grants such an app less (GitLab: `mcp` only), and
   * the owner's own app is the way out.
   */
  readonly missing_scopes: readonly string[];
  /** ISO. When the access token in the secret document expires. */
  readonly access_token_expires_at: string | null;
}

/**
 * The sealed document. Tokens are absent before the first sign-in; a
 * customer's own OAuth app (client id + secret, typed on the settings page)
 * lives here too, so it is scoped to this one connection. A client that
 * dynamic registration issued us lives here for the same reason.
 */
export interface OAuthApiConnectionSecret {
  readonly access_token: string | null;
  readonly refresh_token: string | null;
  readonly client_id: string | null;
  readonly client_secret: string | null;
}

export const sealedOAuthApiSecret = (secret: OAuthApiConnectionSecret): unknown =>
  ({ schema: OAUTH_API_SECRET_SCHEMA, ...secret });

export const parseOAuthApiMetadata = (value: unknown): OAuthApiConnectionMetadata => {
  const m = isRecord(value) ? value : {};
  const source = str(m.client_source);
  return {
    hosts: strings(m.hosts),
    pending_hosts: strings(m.pending_hosts),
    authorization_server: str(m.authorization_server),
    authorization_endpoint: str(m.authorization_endpoint) ?? "",
    token_endpoint: str(m.token_endpoint) ?? "",
    scopes: strings(m.scopes),
    client_source: source === "squidler" || source === "dcr" || source === "customer" ? source : null,
    oauth_client_id: source === "squidler" ? str(m.oauth_client_id) : null,
    docs_url: str(m.docs_url),
    client_needed: m.client_needed === true,
    missing_scopes: strings(m.missing_scopes),
    access_token_expires_at: str(m.access_token_expires_at),
  };
};

/**
 * The sign-in server's identity: its issuer when discovery found one, else the
 * authorize URL's origin. The key a Squidler client and a client request are
 * filed under.
 */
export const authorizationServerOf = (m: Pick<OAuthApiConnectionMetadata, "authorization_server" | "authorization_endpoint">): string => {
  if (m.authorization_server !== null) return m.authorization_server;
  try {
    return new URL(m.authorization_endpoint).origin;
  } catch {
    return m.authorization_endpoint;
  }
};

/** An unknown schema reads as nothing: never a guess at a credential. */
export const parseOAuthApiSecret = (value: unknown): OAuthApiConnectionSecret | null => {
  const schema = schemaOf(value);
  if (schema !== null && schema !== OAUTH_API_SECRET_SCHEMA) return null;
  const m = isRecord(value) ? value : {};
  const nonEmpty = (v: unknown): string | null => {
    const s = str(v);
    return s === null || s === "" ? null : s;
  };
  return {
    access_token: nonEmpty(m.access_token),
    refresh_token: nonEmpty(m.refresh_token),
    client_id: nonEmpty(m.client_id),
    client_secret: nonEmpty(m.client_secret),
  };
};
